import { useEffect, useCallback } from 'react';
import { useGameStore } from '../store/gameStore';
import { calculateLifeScore, getLifeStageForAge, getRandomLifeEvent } from '../utils/gameHelpers';

export const useGameLogic = () => {
  const { 
    currentAge, 
    currentStage, 
    stats, 
    score, 
    timeline, 
    addTimelineEvent, 
    updateStats, 
    advanceAge, 
    unlockAchievement, 
    allAchievements,
    activeHabits,
    habits,
    mandatoryDecisions,
    gamePhase,
  } = useGameStore();

  // Calculate and update score when stats change
  useEffect(() => {
    const newScore = calculateLifeScore(stats);
    useGameStore.setState({ score: newScore });
  }, [stats]);

  // Check for death
  useEffect(() => {
    if (stats.health <= 0 && gamePhase === 'playing') {
      useGameStore.setState({ gamePhase: 'dead', causeOfDeath: 'health' });
      addTimelineEvent({
        id: `death-${currentAge}`,
        age: currentAge,
        type: 'milestone',
        title: 'Game Over',
        description: 'Your health has reached zero. The game ends here.',
        impact: {},
        color: '#dc2626',
      });
    }
  }, [stats.health, gamePhase, currentAge, addTimelineEvent]);

  // Apply habit effects
  useEffect(() => {
    if (activeHabits.length > 0) {
      const habitEffects: Partial<typeof stats> = {};
      
      activeHabits.forEach(habitId => {
        const habit = habits.find(h => h.id === habitId);
        if (habit && habit.isActive) {
          Object.entries(habit.statEffects).forEach(([key, value]) => {
            if (value !== undefined) {
              habitEffects[key as keyof typeof stats] = (habitEffects[key as keyof typeof stats] || 0) + value;
            }
          });
        }
      });

      if (Object.keys(habitEffects).length > 0) {
        const newStats = { ...stats };
        Object.entries(habitEffects).forEach(([key, value]) => {
          if (value !== undefined) {
            newStats[key as keyof typeof stats] = Math.max(0, Math.min(100, 
              newStats[key as keyof typeof stats] + value
            ));
          }
        });
        updateStats(habitEffects);
      }
    }
  }, [activeHabits, habits, stats, updateStats]);

  // Check for achievement unlocks
  useEffect(() => {
    const checkAchievements = () => {
      const { unlockedAchievements } = useGameStore.getState();
      
      // Check each achievement
      allAchievements.forEach(achievement => {
        if (unlockedAchievements.includes(achievement.id)) return;

        let shouldUnlock = false;

        // Check stat requirements
        if (achievement.requirements.stats) {
          shouldUnlock = Object.entries(achievement.requirements.stats).every(
            ([stat, required]) => stats[stat as keyof typeof stats] >= (required || 0)
          );
        }

        // Check age requirements
        if (achievement.requirements.age && currentAge >= achievement.requirements.age) {
          shouldUnlock = true;
        }

        // Check decision requirements
        if (achievement.requirements.decisions) {
          const completedDecisions = useGameStore.getState().completedDecisions;
          shouldUnlock = achievement.requirements.decisions.every(decisionId =>
            completedDecisions.includes(decisionId)
          );
        }

        if (shouldUnlock) {
          unlockAchievement(achievement.id);
        }
      });
    };

    checkAchievements();
  }, [stats, currentAge, allAchievements, unlockAchievement]);

  // Add random life events periodically
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.1) { // 10% chance every interval
        const event = getRandomLifeEvent(currentAge);
        addTimelineEvent({
          id: `random-event-${Date.now()}`,
          age: currentAge,
          type: 'milestone',
          title: 'Life Event',
          description: event,
          impact: {},
          color: '#6b7280',
        });
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, [currentAge, addTimelineEvent]);

  // Handle age progression effects
  const handleAgeProgression = useCallback(() => {
    // Check if there are mandatory decisions that need to be made
    if (mandatoryDecisions.length > 0) {
      console.log('Cannot advance age: mandatory decisions pending', mandatoryDecisions);
      return;
    }

    const newAge = currentAge + 1;
    const newStage = getLifeStageForAge(newAge);

    // Age-related stat changes
    const ageEffects: Partial<typeof stats> = {};

    // More gradual health decline
    if (newAge > 40) {
      ageEffects.health = Math.max(0, stats.health - 0.5);
    }
    if (newAge > 60) {
      ageEffects.health = Math.max(0, stats.health - 1);
    }
    if (newAge > 80) {
      ageEffects.health = Math.max(0, stats.health - 1.5);
    }

    // Career progression
    if (newAge >= 25 && newAge <= 50) {
      ageEffects.career = Math.min(100, stats.career + 1);
    }

    // Update stats
    if (Object.keys(ageEffects).length > 0) {
      updateStats(ageEffects);
    }

    // Add milestone event
    if (newAge % 10 === 0) {
      addTimelineEvent({
        id: `milestone-${newAge}`,
        age: newAge,
        type: 'milestone',
        title: `${newAge}th Birthday`,
        description: `You've reached a new decade of life!`,
        impact: ageEffects,
        color: '#f59e0b',
      });
    }

    // Stage transition
    if (newStage !== currentStage) {
      addTimelineEvent({
        id: `stage-transition-${newAge}`,
        age: newAge,
        type: 'milestone',
        title: 'Life Stage Transition',
        description: `You've entered a new phase of life: ${newStage}`,
        impact: {},
        color: '#8b5cf6',
      });

      // Add new decisions for the new life stage
      const { decisions, availableDecisions, completedDecisions, mandatoryDecisions } = useGameStore.getState();
      const stageDecisions = decisions.filter(d => {
        const stageMatch = d.stage === newStage || d.stage === 'any';
        const ageMatch = (!d.minAge || newAge >= d.minAge) && (!d.maxAge || newAge <= d.maxAge);
        return stageMatch && ageMatch;
      });
      const newDecisions = stageDecisions
        .filter(d => !completedDecisions.includes(d.id) && !availableDecisions.includes(d.id))
        .map(d => d.id);
      
      // Separate mandatory and optional decisions
      const mandatoryStageDecisions = stageDecisions
        .filter(d => d.mandatory && !completedDecisions.includes(d.id))
        .map(d => d.id);
      
      if (newDecisions.length > 0) {
        useGameStore.setState({
          availableDecisions: [...availableDecisions, ...newDecisions.filter(d => !mandatoryStageDecisions.includes(d))],
          mandatoryDecisions: [...mandatoryDecisions, ...mandatoryStageDecisions],
          gamePhase: mandatoryStageDecisions.length > 0 ? 'paused' : 'playing'
        });
      }
    }

    // Add random decisions based on age and current situation
    const { decisions, availableDecisions, completedDecisions } = useGameStore.getState();
    const randomDecisions = decisions.filter(d => 
      d.stage === 'any' && 
      !completedDecisions.includes(d.id) && 
      !availableDecisions.includes(d.id) &&
      Math.random() < 0.2 // 20% chance each year
    );
    
    if (randomDecisions.length > 0) {
      const randomDecision = randomDecisions[Math.floor(Math.random() * randomDecisions.length)];
      useGameStore.setState({
        availableDecisions: [...availableDecisions, randomDecision.id]
      });
    }

    // Add age-specific random decisions
    if (newAge === 25 || newAge === 30 || newAge === 40 || newAge === 50 || newAge === 60) {
      const ageSpecificDecisions = decisions.filter(d => 
        d.stage === 'any' && 
        !completedDecisions.includes(d.id) && 
        !availableDecisions.includes(d.id)
      );
      
      if (ageSpecificDecisions.length > 0) {
        const ageDecision = ageSpecificDecisions[Math.floor(Math.random() * ageSpecificDecisions.length)];
        useGameStore.setState({
          availableDecisions: [...availableDecisions, ageDecision.id]
        });
      }
    }

    // Special relationship decisions around age 25
    if (newAge === 25) {
      const relationshipDecisions = decisions.filter(d => 
        d.id === 'decision-relationship-1' && 
        !completedDecisions.includes(d.id) && 
        !availableDecisions.includes(d.id)
      );
      
      if (relationshipDecisions.length > 0) {
        useGameStore.setState({
          availableDecisions: [...availableDecisions, 'decision-relationship-1']
        });
      }
    }

    advanceAge();
  }, [currentAge, currentStage, stats, updateStats, addTimelineEvent, advanceAge]);

  // Game completion check
  useEffect(() => {
    if (currentAge >= 100) {
      useGameStore.setState({ gamePhase: 'completed' });
    }
  }, [currentAge]);

  return {
    handleAgeProgression,
  };
};
